import React from 'react'
import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import TrackEditorModal from './TrackEditorModal';
import SearchIcon from '@mui/icons-material/Search';
import SchoolIcon from '@mui/icons-material/School';
import { TrainingEmptyState } from './TrainingEmptyState';
import LearningContentPreviewModal from "./LearningContentPreviewModal";
import LearningPreviewMenuButton from "./LearningPreviewMenuButton";
import { formatDate } from "../requests/utilities/helpers";

const filterSelectSx = {
  height: 42,
  minWidth: 170,
  borderRadius: "8px",
  fontSize: "14px",
  backgroundColor: "#fff",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#a78bfa" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
  "& .MuiSelect-select": {
    py: "9px",
    display: "flex",
    alignItems: "center",
  },
};

function TracksDisplay(props) {

  const [openEditor, setOpenEditor] = React.useState(false);
  const [addMenuAnchor, setAddMenuAnchor] = React.useState(null);
  const [previewTrack, setPreviewTrack] = React.useState(null);
  const handleOpenEditor = () => setOpenEditor(true);
  const handleCloseEditor = () => setOpenEditor(false);
  const { tracksData, modulesData, sectionsData, setModalDataTrack } = props;

  // Track the open track by UUID, not by list index. filteredTracks hides
  // direct-assignment wrappers, so a displayed index no longer maps to the same
  // position in the unfiltered props.tracksData — indexing by it on refresh
  // could swap in the wrong (hidden wrapper) track.
  const [selectedTrackUuid, setSelectedTrackUuid] = React.useState(null)

  const [searchInput, setSearchInput] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [roleFilter, setRoleFilter] = React.useState("all");
  const [sortKey, setSortKey] = React.useState("recent");

  const calculateTrackMetrics = React.useCallback((track) => {
    const totalModules = track.modules?.length || 0;
    const totalSections = track.modules?.reduce((acc, module) => acc + (module.sections?.length || 0), 0) || 0;
    return { totalModules, totalSections };
  }, []);

  const filteredTracks = React.useMemo(() => {
    const searchParts = searchInput.toLowerCase().split(" ").filter(Boolean);
    const authoredTracks = (tracksData || []).filter((track) => {
      // Hide auto-synthesized direct-assignment wrappers — they're not authored
      // tracks. They surface only in Training Overview as assignable items.
      if (track?.assignment_type && track.assignment_type !== "track") {
        return false;
      }

      const isDraft = track.status === "draft";
      const hasRoles = (track.roles_assigned_to || []).length > 0;
      const haystack = `${track?.name || ""} ${track?.description || ""} ${
        (track.roles_assigned_to || []).map((role) => role.role_name).join(" ")
      }`.toLowerCase();

      if (!searchParts.every((part) => haystack.includes(part))) {
        return false;
      }
      if (statusFilter === "draft" && !isDraft) {
        return false;
      }
      if (statusFilter === "published" && isDraft) {
        return false;
      }
      if (roleFilter === "assigned" && !hasRoles) {
        return false;
      }
      if (roleFilter === "unassigned" && hasRoles) {
        return false;
      }
      return true;
    });

    return [...authoredTracks].sort((a, b) => {
      if (sortKey === "name") {
        return (a.name || "").localeCompare(b.name || "");
      }
      if (sortKey === "modules") {
        return calculateTrackMetrics(b).totalModules - calculateTrackMetrics(a).totalModules;
      }
      if (sortKey === "sections") {
        return calculateTrackMetrics(b).totalSections - calculateTrackMetrics(a).totalSections;
      }
      return (b.updated_at || b.created_at || 0) - (a.updated_at || a.created_at || 0);
    });
  }, [tracksData, searchInput, statusFilter, roleFilter, sortKey, calculateTrackMetrics]);

  const activeFilterCount =
    (statusFilter !== "all" ? 1 : 0) +
    (roleFilter !== "all" ? 1 : 0);
  const hasSearchOrFilters = Boolean(searchInput || activeFilterCount);
  const noResultsBlurb = searchInput && activeFilterCount
    ? "Try adjusting your search or clearing the selected filters."
    : activeFilterCount
      ? "Try changing the status or role filters."
      : "Try adjusting your search terms.";

  const clearSearchAndFilters = () => {
    setSearchInput("");
    setStatusFilter("all");
    setRoleFilter("all");
  };

  React.useEffect(() => {
    if (!selectedTrackUuid) return;
    // Re-resolve the open track from the refreshed data by UUID so the editor
    // always shows/saves the same track regardless of its position in the list.
    const refreshedTrack = tracksData?.find(
      (t) => t?.uuid === selectedTrackUuid
    );
    if (refreshedTrack) {
      setModalDataTrack(refreshedTrack);
    }
  }, [tracksData, modulesData, sectionsData, selectedTrackUuid, setModalDataTrack]);

  return (
    <>
      <div className="min-h-screen bg-white">
        {/* Header Section */}
        <div className="bg-white border-b border-gray-200">
          <div className="px-6 py-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-slate-900 mb-1">Learning Tracks</h1>
                <p className="text-sm text-slate-600">Structured learning paths for your team</p>
              </div>
              <button
                className="bg-violet-700 hover:bg-violet-800 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
                onClick={(e) => setAddMenuAnchor(e.currentTarget)}
              >
                <AddIcon style={{ fontSize: "20px" }} />
                <span className="font-medium">New Track</span>
                <KeyboardArrowDownIcon style={{ fontSize: "18px" }} />
              </button>
              <Menu
                anchorEl={addMenuAnchor}
                open={Boolean(addMenuAnchor)}
                onClose={() => setAddMenuAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <MenuItem
                  onClick={() => {
                    setAddMenuAnchor(null);
                    props.setModalDataTrack(props.default_data_track);
                    handleOpenEditor();
                  }}
                >
                  <ListItemIcon>
                    <AddIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="New track" secondary="Build it yourself" />
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setAddMenuAnchor(null);
                    props.onGenerateWithAi && props.onGenerateWithAi();
                  }}
                >
                  <ListItemIcon>
                    <AutoAwesomeIcon fontSize="small" sx={{ color: "#6d28d9" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Generate with AI"
                    secondary="Draft a track from a prompt and your docs"
                  />
                </MenuItem>
              </Menu>
            </div>

            <div className="flex items-center gap-6 text-sm text-slate-600">
              <div className="flex items-center gap-2">
                <SchoolIcon style={{ fontSize: "18px" }} />
                <span>{filteredTracks?.length || 0} Tracks</span>
              </div>
              {activeFilterCount > 0 && (
                <div className="flex items-center gap-2 text-violet-700">
                  <span>{activeFilterCount} filter active</span>
                </div>
              )}
              {searchInput && (
                <div className="flex items-center gap-2 text-violet-600">
                  <SearchIcon style={{ fontSize: "16px" }} />
                  <span>Showing search results</span>
                </div>
              )}
            </div>

            {/* Search and Stats */}
            <div className="flex flex-wrap items-center gap-3 mt-6">
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <SearchIcon
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                    style={{ fontSize: "20px" }}
                  />
                  <input
                    className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-200 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-100 text-sm"
                    type="text"
                    placeholder="Search learning tracks..."
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                  />
                </div>
              </div>
              <Select
                size="small"
                sx={filterSelectSx}
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                <MenuItem value="all" sx={{ fontSize: 14 }}>
                  All statuses
                </MenuItem>
                <MenuItem value="published" sx={{ fontSize: 14 }}>
                  Published
                </MenuItem>
                <MenuItem value="draft" sx={{ fontSize: 14 }}>
                  Draft
                </MenuItem>
              </Select>
              <Select
                size="small"
                sx={filterSelectSx}
                value={roleFilter}
                onChange={(event) => setRoleFilter(event.target.value)}
              >
                <MenuItem value="all" sx={{ fontSize: 14 }}>
                  All roles
                </MenuItem>
                <MenuItem value="assigned" sx={{ fontSize: 14 }}>
                  Assigned to roles
                </MenuItem>
                <MenuItem value="unassigned" sx={{ fontSize: 14 }}>
                  No roles assigned
                </MenuItem>
              </Select>
              <Select
                size="small"
                sx={filterSelectSx}
                value={sortKey}
                onChange={(event) => setSortKey(event.target.value)}
              >
                <MenuItem value="recent" sx={{ fontSize: 14 }}>
                  Recently updated
                </MenuItem>
                <MenuItem value="name" sx={{ fontSize: 14 }}>
                  Name
                </MenuItem>
                <MenuItem value="modules" sx={{ fontSize: 14 }}>
                  Most modules
                </MenuItem>
                <MenuItem value="sections" sx={{ fontSize: 14 }}>
                  Most sections
                </MenuItem>
              </Select>
            </div>
          </div>
        </div>

        {/* Tracks Grid */}
        <div className="px-6 py-8 bg-white min-h-screen">
          {filteredTracks?.length === 0 ? (
            <TrainingEmptyState
              icon={SchoolIcon}
              plural="tracks"
              blurb="Group your modules into a learning track your team works through."
              createLabel="Create your first track"
              searching={hasSearchOrFilters}
              noResultsTitle="No tracks match your filters"
              noResultsBlurb={noResultsBlurb}
              clearLabel="Clear filters"
              onClear={hasSearchOrFilters ? clearSearchAndFilters : undefined}
              onCreate={() => {
                props.setModalDataTrack(props.default_data_track);
                handleOpenEditor();
              }}
            />
          ) : (
            <div className="grid grid-cols-3 xl:grid-cols-2 md:grid-cols-1 gap-6">
              {filteredTracks?.map((track, index) => {
                const metrics = calculateTrackMetrics(track);
                const firstModuleColor =
                  track.modules?.[0]?.color || "bg-slate-400";
                const moduleDots = (track.modules || []).slice(0, 8);
                const roles = (track.roles_assigned_to || [])
                  .map((r) => r.role_name)
                  .slice(0, 3)
                  .join(" · ");
                const extraRoles =
                  (track.roles_assigned_to?.length || 0) - 3;
                const updatedLabel = track.updated_at
                  ? `Updated ${formatDate(track.updated_at)}`
                  : "";

                return (
                  <div
                    key={index}
                    onClick={() => {
                      props.setModalDataTrack(track);
                      setSelectedTrackUuid(track.uuid);
                      handleOpenEditor();
                    }}
                    className="bg-white rounded-xl border border-slate-200 hover:border-slate-300 hover:shadow-lg transition-all duration-200 overflow-hidden cursor-pointer group"
                  >
                    {/* Hero */}
                    <div className="relative aspect-[16/9] w-full overflow-hidden bg-slate-100">
                      <div className={`absolute inset-0 ${firstModuleColor}`} />
                      {track.cover?.url && (
                        <img
                          src={track.cover.url}
                          alt={track.name}
                          className="absolute inset-0 w-full h-full object-cover"
                          draggable={false}
                          onError={(e) => {
                            // Hide the broken image; the color fallback
                            // div behind it will show through.
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      )}
                      {track.status === "draft" && (
                        <div className="absolute top-2 left-2 z-10 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold ring-1 ring-amber-300">
                          Draft
                        </div>
                      )}
                      <div className="absolute right-2 top-2 z-20 opacity-0 transition-opacity group-hover:opacity-100">
                        <LearningPreviewMenuButton
                          onSelect={(device) => setPreviewTrack({ item: track, device })}
                          iconButtonSx={{
                            backgroundColor: "rgba(255,255,255,0.92)",
                            "&:hover": { backgroundColor: "white" },
                          }}
                        />
                      </div>
                      {/* Bottom scrim for title legibility */}
                      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                      <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                        <h3 className="text-lg font-semibold line-clamp-2 drop-shadow-sm">
                          {track.name}
                        </h3>
                        {roles ? (
                          <p className="text-xs text-white/85 mt-1 line-clamp-1">
                            {roles}
                            {extraRoles > 0 ? ` · +${extraRoles} more` : ""}
                          </p>
                        ) : (
                          <p className="text-xs text-white/70 mt-1">
                            No roles assigned
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Body */}
                    <div className="p-4">
                      {track.description ? (
                        <p className="text-sm text-slate-600 line-clamp-2">
                          {track.description}
                        </p>
                      ) : (
                        <p className="text-sm text-slate-400 italic">
                          No description yet
                        </p>
                      )}

                      <div className="mt-4 flex items-center justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-3">
                          <div className="flex items-center gap-1">
                            {moduleDots.length > 0 ? (
                              moduleDots.map((m, i) => (
                                <div
                                  key={i}
                                  className={`w-2 h-2 rounded-full ${
                                    m.color || "bg-slate-300"
                                  }`}
                                />
                              ))
                            ) : (
                              <div className="w-2 h-2 rounded-full bg-slate-200" />
                            )}
                          </div>
                          <p className="truncate text-xs text-slate-500">
                            {metrics.totalModules}{" "}
                            {metrics.totalModules === 1 ? "module" : "modules"}{" "}
                            · {metrics.totalSections}{" "}
                            {metrics.totalSections === 1 ? "section" : "sections"}
                          </p>
                        </div>
                        {updatedLabel && (
                          <span className="shrink-0 text-xs text-slate-400">
                            {updatedLabel}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <TrackEditorModal
        open={openEditor}
        handleClose={handleCloseEditor}
        modalDataTrack={props.modalDataTrack}
        setModalDataTrack={props.setModalDataTrack}
        homeCompany={props.homeCompany}
        homeStore={props.homeStore}
        modalDataModule={props.modalDataModule}
        setModalDataModule={props.setModalDataModule}
        modalDataSection={props.modalDataSection}
        setModalDataSection={props.setModalDataSection}
        quizzesData={props.quizzesData}
        default_data_module={props.default_data_module}
        default_data_section={props.default_data_section}
        modulesData={props.modulesData}
        sectionsData={props.sectionsData}
        tracksData={props.tracksData}
        roleNames={props.roleNames}
        post_learning_track={props.post_learning_track}
        delete_learning_track={props.delete_learning_track}
        post_learning_module={props.post_learning_module}
        delete_learning_module={props.delete_learning_module}
        post_learning_section={props.post_learning_section}
        delete_learning_section={props.delete_learning_section}
        modalDataQuiz={props.modalDataQuiz}
        setModalDataQuiz={props.setModalDataQuiz}
        default_data_quiz={props.default_data_quiz}
        post_learning_quiz={props.post_learning_quiz}
        post_learning_track_enrollment_by_roles={props.post_learning_track_enrollment_by_roles}
      />
      <LearningContentPreviewModal
        open={Boolean(previewTrack)}
        onClose={() => setPreviewTrack(null)}
        previewType="track"
        item={previewTrack?.item}
        initialDevice={previewTrack?.device}
        homeCompany={props.homeCompany}
        quizzesData={props.quizzesData}
      />
    </>
  )
}

export default TracksDisplay
